// libraries
import { createDefaultMarker, EHereMapMarkerType, IHereMarkerInit } from "@algo/here-maps";
import React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// hooks & context
import { getCoords as getGroupedCameraCoords } from "~/store/algo-api/slices/camera-group";
// types & models
import { EAlgoLayerType, ICoord, IMarkerTapObject, IZoomToValue } from "~/interfaces";
// resources
import { getAllGrouped as getAllCamerasGrouped } from "~/store/algo-api/slices/camera-group";
import { filterGroupedBySearch as filterCameraGroups } from "~/store/algo-api/slices/camera-group";
import { nuonoe } from "@caps-mobile/common-lib";
import { mapLayerToPin } from "~/resources/icons/algo-traffic-icons/map-pin";


interface UseCameraGroupProps {
    searchText: string | null;
    map: any;
    markerTapHandler: (dataObject: IMarkerTapObject) => void;
}
  
export const useCameraGroup = ({ searchText, map, markerTapHandler }: UseCameraGroupProps) => {

    const dispatch = useDispatch();

    // Dispatch the getAllCamerasGrouped action
    useEffect(() => {
        if(!searchText)
            dispatch(getAllCamerasGrouped());
        else
            dispatch(filterCameraGroups(searchText) as any);
    }, [searchText]);
  
    // Use the custom useMarkerGroup hook
    const cameraGroupMarkerGroup = useMarkerGroup(map, markerTapHandler, nuonoe(searchText));
  
    if (map && cameraGroupMarkerGroup) {
        map.addObject(cameraGroupMarkerGroup);
    }
};

export const buildMarkerInits = (
    dataList: any[], 
    getCoords: (object: any) => { lat: number, lng: number} | null,
): IHereMarkerInit[] => {

    if (!dataList) return [];

    let markerInits: IHereMarkerInit[] = [];

    if (!nuonoe(dataList)) return markerInits;

    let icon: any = null;

    for (let i = 0; i < dataList.length; i++){

        let nextObject: any = dataList[i];

        let type: EHereMapMarkerType = EHereMapMarkerType.Default;
        let pinResource: any;
        let crossOrigin: boolean = false;

        pinResource = mapLayerToPin(EAlgoLayerType["camera-group"]);
        
        let nextCoords: H.geo.Point = getCoords(nextObject) as H.geo.Point;
            
        icon = {
            iconSrc: pinResource,
            size: {w: 23, h: 23},
            crossOrigin
        };

        markerInits.push({
            type,
            position: nextCoords,
            options: {icon},
            data: {
                type: EAlgoLayerType["camera-group"],
                data: nextObject
            }
        });
    }

    return markerInits;
};

export const useMarkerGroup = (
    map: H.Map | undefined, 
    markerTapCallback?: (markerData: IMarkerTapObject) => void,
    isSearch?: boolean
): H.map.Group | null => {

    // get the respective data and checksum
    const dataStore: any = useSelector( (state: any) => state["camera-group"]);
    const data: any = isSearch ? dataStore.dataGroupedFilteredBySearch : dataStore.data;

    // keep a list of created markers
    const [ markerGroup, setMarkerGroup ] = React.useState<H.map.Group | null>(null);

    useEffect(
        () => {
            // only execute logic if data exists and 
            // either checksum has changed or no markerGroup exists
            if (
                map
            ){
                // determine which function to use to extract object coords
                let getCoords: (item: any) => ICoord | null = getGroupedCameraCoords;

                // build the list of marker inits
                let markerInits: IHereMarkerInit[] = 
                    buildMarkerInits(data, getCoords);

                // create a new marker group to hold the new marker objects if necessary
                let newMarkerGroup: H.map.Group = markerGroup || new window.H.map.Group();

                // remove all the current marker group if anything exists
                newMarkerGroup.forEach( (marker) => {marker.dispose();})
                newMarkerGroup.removeAll();

                // create marker object for each init and append them to the new group
                markerInits.forEach( (markerInit: IHereMarkerInit) => {
                    let marker: H.map.Marker | null = createDefaultMarker(markerInit);
                    marker?.setData(markerInit.data);

                    marker?.addEventListener('tap', (evt: any) => { 
                        markerTapCallback && markerTapCallback(evt.target.getData())
                    });

                    if (marker) {
                        newMarkerGroup.addObject(marker);
                    }
                });

                // update the group state
                setMarkerGroup(newMarkerGroup);
            }

            // cleanup logic
            return () => {
                //
            }

        }, [map, data]
    );

    // return the current marker group state
    return markerGroup;
};

// causes the map view to resize after the nav panel animation
export const useResizeMapOnNavToggle = (map: H.Map | undefined, panelOpen: boolean) => {

    useEffect( 
        () => { 
            if (map)
                setTimeout( () => map.getViewPort().resize(), 500)
        }, [panelOpen]
    );

};

// causes the map view to resize and sets zoom after window resize
export const useWindowResize = (map: H.Map | undefined, width: number, height: number, zoomTarget: IZoomToValue, isMediumMax?: boolean) => {
    
    // lat and lng of current location
    const [lat, setLat] = React.useState<number>();
    const [lng, setLng] = React.useState<number>();
    navigator.geolocation.getCurrentPosition(
        (position) => {
            setLat(position.coords.latitude);
            setLng(position.coords.longitude);
        }
    );
    
    useEffect( 
        () => { 
            if (map){
                setTimeout( () => {map.getViewPort().resize()
                if(zoomTarget.bounds){
                    var bbox = new H.geo.Rect(
                        isMediumMax ? zoomTarget.bounds?.top + 1 : zoomTarget.bounds?.top, 
                        zoomTarget.bounds?.left, 
                        zoomTarget.bounds?.bottom, 
                        zoomTarget.bounds?.right);
                    map.getViewModel().setLookAtData({
                        bounds: bbox
                    });
                }
                else if(zoomTarget.center){
                    map.setZoom(zoomTarget.zoom);
                    map.setCenter(zoomTarget.center);
                }
                else if(lat && lng){
                    map.setZoom(zoomTarget.zoom);
                    map.setCenter({lat: lat, lng: lng});
                }}, 0)
            }
        }, [width, height]
    );

}

// adds listener to the map that changes cursor style to pointer when
// hovering over a marker

function mapCursorListener(event: any, map: H.Map) {
    if (event.target instanceof H.map.Marker || event.target instanceof H.map.Polygon || event.target instanceof H.map.Polyline) {
        (map.getViewPort().element as any).style.cursor = 'pointer';
    } else {
        (map.getViewPort().element as any).style.cursor = 'auto';
    }
}

export const useCursorOnFeature = (map: H.Map | undefined) => {

    useEffect(
        () => {

            if (map){

                map.addEventListener('pointermove', (evt) => mapCursorListener(evt, map), false);
    
                return () => {
                    map.removeEventListener('pointermove', (evt) => mapCursorListener(evt, map), false);
                }
            }

        }, [map]
    );
};

export const useSetZoomAndCenter = (
    mapAndPlatform: [H.Map | undefined, H.service.Platform | undefined], 
    zoomTarget: IZoomToValue, 
    zoomToggled: boolean = false,
    isMediumMax?: boolean
) => {

    // lat and lng of current location
    const [lat, setLat] = React.useState<number>();
    const [lng, setLng] = React.useState<number>();
    navigator.geolocation.getCurrentPosition(
        (position) => {
            setLat(position.coords.latitude);
            setLng(position.coords.longitude);
        }
    );

    useEffect(
        () => {
            let map: H.Map | undefined = mapAndPlatform[0];
            let platform: H.service.Platform | undefined = mapAndPlatform[1];

            if (map && platform){
                //If entire state
                if(zoomTarget.bounds){
                    var bbox = new H.geo.Rect(
                        isMediumMax ? zoomTarget.bounds?.top + 1 : zoomTarget.bounds?.top, 
                        zoomTarget.bounds?.left, 
                        zoomTarget.bounds?.bottom, 
                        zoomTarget.bounds?.right);
                    map.getViewModel().setLookAtData({
                        bounds: bbox
                    });
                }
                else if(zoomTarget.center){
                    map.setZoom(zoomTarget.zoom);
                    map.setCenter(zoomTarget.center);
                }
                else if(lat && lng){
                    map.setZoom(zoomTarget.zoom);
                    map.setCenter({lat: lat, lng: lng});
                }
            }

        }, [mapAndPlatform[0], mapAndPlatform[1], zoomTarget, zoomToggled]
    );
};